import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Box } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import {
  tableSelectionOptions,
  defaultTableOptions,
  tableCellularEditingOptions,
  defaultTableInitialState,
} from '../../util/data-table';
import { unique } from '../../../utils/collections';
import getBuildingStaging from '../../../redux/selectors/staging';
import EquipmentDetails from '../panels/equipment/EquipmentDetails';
import {
  publishEquipmentAction,
  updateStagingPoints,
  updateStagingEquipment,
  stagingEquipmentDelete,
  unpublishStagingAction,
  createStagingEquipment,
} from '../../../redux/actions/staging';
import {
  equipmentColumns,
  equipmentSuperColumn,
  equipmentNameId,
  equipmentTypeId,
  equipmentConfidenceId,
  equipmentDevicesNamesId,
  equipmentDevicesModelsId,
  lastPublishedCol,
} from '../columns';
import { deleteEquipmentConfirm, unpublishConfirm } from '../dialogs/confirmations';
import EquipmentTypeSelector from '../selectors/EquipmentTypeSelector';
import EnumSelectorDialog from '../dialogs/EnumSelectorDialog';
import PublishDialog from '../dialogs/PublishDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { computeUnpublishedPointsForEquipment, stagingEquipmentDiff } from '../staging';
import CreateEntity from '../dialogs/CreateEntity';

const Equipment = ({
  building,

  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,

  equipmentLoadTime,
  stagingPoints,
  stagingEquipment,
  canWrite,
  equipmentTypes,

  stagingEquipmentCreate,
  stagingEquipmentUpdates,
  stagingPointUpdates,
  publishEquipment,
  unpublish,
  deleteStagingEquipment,
}) => {
  const [equipmentUpdateEquipmentTypes, setEquipmentUpdateEquipmentTypes] = useState([]);
  const [equipmentToPublish, setEquipmentToPublish] = useState([]);
  const [confirmation, setConfirmation] = useState({});

  const stagingNames = useMemo(() => stagingEquipment.map((e) => e.name), [stagingEquipment]);

  const columns = useMemo(
    () => equipmentColumns(canWrite, equipmentTypes, stagingNames, stagingEquipmentUpdates),
    [canWrite, equipmentTypes, stagingNames, stagingEquipmentUpdates]
  );

  const data = useMemo(
    () =>
      stagingEquipment.map((e) => ({
        ...e,
        diff: stagingEquipmentDiff(e),
      })),
    [stagingEquipment]
  );

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const equipmentSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const equipmentColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    ...tableCellularEditingOptions,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: equipmentColumnFilters,
        sorting: equipmentSorting,
        hiddenColumns: columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![
                equipmentSuperColumn.id,
                equipmentNameId,
                equipmentTypeId,
                equipmentConfidenceId,
                equipmentDevicesNamesId,
                equipmentDevicesModelsId,
                lastPublishedCol.id,
              ].includes(id)
          ),
        forcedHiddenColumns: equipmentSuperColumn.subColumns.map(({ id }) => id),
      }),
    },
    getRowId: (e) => e.equipId,
    enableRowSelection: () => canWrite,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: equipmentSorting,
      columnFilters: equipmentColumnFilters,
      showAlertBanner: true,
      isLoading: !equipmentLoadTime,
    },
    enableEditing: canWrite,
    // Only allow one expanded at a time
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }),
    }),
    renderDetailPanel: ({ row }) => (
      <EquipmentDetails
        building={building}
        stagingEquipment={stagingEquipment}
        stagingPoints={stagingPoints}
        equipment={row.original}
        canWrite={canWrite}
      />
    ),
    renderCreateRowDialogContent: ({ table }) => (
      <CreateEntity
        usedNames={stagingNames}
        submit={(target) => stagingEquipmentCreate(target.entityName.value)}
        onExit={() => table.setCreatingRow(false)}
      />
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      const handleEquipmentType = () => {
        setEquipmentUpdateEquipmentTypes(
          table.getSelectedRowModel().flatRows.map(({ original }) => original)
        );
      };

      const handlePublish = () =>
        setEquipmentToPublish(table.getSelectedRowModel().flatRows.map(({ original }) => original));

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            color="success"
            disabled={!canWrite}
            onClick={() => table.setCreatingRow(true)}
            variant="contained"
          >
            Create
          </Button>
          <Button
            color="success"
            disabled={!canWrite || table.getSelectedRowModel().flatRows.length <= 0}
            onClick={handleEquipmentType}
            variant="contained"
          >
            Update Equipment Type
          </Button>
          <Button
            color="success"
            disabled={!canWrite || table.getSelectedRowModel().flatRows.length <= 0}
            onClick={handlePublish}
            variant="contained"
          >
            Publish
          </Button>
          <Button
            color="error"
            disabled={
              !canWrite ||
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table.getSelectedRowModel().flatRows?.every(({ original }) => original.published)
            }
            onClick={() =>
              setConfirmation({
                id: 'reset',
                content: `This will revert ${table.getSelectedRowModel().flatRows.length} equipment to their state at the time of last publish. This includes point assignments, equipment type, and equipment name.`,
                onExit: () => setConfirmation({}),
                onSuccess: () => {
                  const publishedStagingEquip = table
                    .getSelectedRowModel()
                    .flatRows.map(({ original }) => original)
                    .filter(({ published }) => Boolean(published));
                  const relevantEquipIds = publishedStagingEquip.map((e) => e.equipId);
                  const relatedPoints = unique(
                    publishedStagingEquip.flatMap((e) => e.points),
                    (p) => p.topic
                  );
                  return stagingEquipmentUpdates(
                    publishedStagingEquip.map((e) => ({
                      equip_id: e.equipId,
                      equip_name: e.published.name,
                      equipment_type: { id: e.published.equipmentTypeId, confidence: 100 },
                    }))
                  ).then(() =>
                    stagingPointUpdates(
                      relatedPoints
                        .map((p) => {
                          const publishedEquipIds = p.published?.equipIds ?? [];
                          // filter down equipIds to only those that are published or those that are for unrelated equipIds
                          const newStagingEquipIds = p.equipIds.filter(
                            (equipId) =>
                              publishedEquipIds.includes(equipId) ||
                              !relevantEquipIds.includes(equipId)
                          );
                          // Since this is only subtractive, we can see if there's a change just by checking length
                          if (newStagingEquipIds.length === p.equipIds.length) return {};
                          return {
                            topic: p.topic,
                            equip_ids: newStagingEquipIds,
                          };
                        })
                        .filter((entry) => Object.keys(entry).length > 0)
                    )
                  );
                },
              })
            }
            variant="contained"
          >
            Reset to Last Published
          </Button>
          <Button
            color="error"
            disabled={
              !canWrite ||
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table.getSelectedRowModel().flatRows?.every(({ original }) => original.published) ||
              computeUnpublishedPointsForEquipment(
                table.getSelectedRowModel().flatRows.map(({ original }) => original)
              ) <= 0
            }
            onClick={() => {
              const equipment = table
                .getSelectedRowModel()
                .flatRows.map(({ original }) => original);
              const equipIds = equipment.map((e) => e.equipId);
              const points = computeUnpublishedPointsForEquipment(equipment);

              return setConfirmation({
                id: 'prune',
                content: `${points.length} points will be impacted. This will remove all unpublished point assignments from the selected equipment.`,
                onExit: () => setConfirmation({}),
                onSuccess: () =>
                  stagingPointUpdates(
                    points.map((p) => ({
                      topic: p.topic,
                      // Take the current published set and append the unpublished set that ISN'T the selected equipment and then unique it
                      equip_ids: !p.published
                        ? []
                        : unique(
                            p.published.equipIds.concat(
                              p.equipIds.filter((eid) => !equipIds.includes(eid))
                            ),
                            (e) => e.equipId
                          ),
                    }))
                  ),
              });
            }}
            variant="contained"
          >
            Prune Unpublished Points
          </Button>
          <Button
            color="error"
            disabled={
              !canWrite ||
              table.getSelectedRowModel().flatRows.length <= 0 ||
              !table.getSelectedRowModel().flatRows?.every(({ original }) => original.published)
            }
            onClick={() => {
              const equipment = table
                .getSelectedRowModel()
                .flatRows.map(({ original }) => original);
              const equipmentIds = equipment?.map((e) => e.published.id);
              const involvedPoints = unique(
                equipment.flatMap((e) => e.published.points),
                (p) => p.topic
              );
              const pointIds = involvedPoints
                .filter(
                  (p) => p.equipment_ids.filter((eid) => !equipmentIds.includes(eid)).length === 0
                )
                .map((p) => p.id);
              const assignments = involvedPoints.flatMap((p) =>
                p.equipment_ids
                  .filter((eid) => equipmentIds.includes(eid))
                  .map((eid) => ({ point_id: p.id, equipment_id: eid }))
              );
              return setConfirmation({
                ...unpublishConfirm(unpublish, equipmentIds, pointIds, assignments),
                onExit: () => setConfirmation({}),
              });
            }}
            variant="contained"
          >
            Unpublish
          </Button>
          <Button
            color="error"
            disabled={
              !canWrite ||
              table.getSelectedRowModel().flatRows.length <= 0 ||
              table.getSelectedRowModel().flatRows?.some(({ original }) => original.published)
            }
            onClick={() =>
              setConfirmation({
                ...deleteEquipmentConfirm(
                  deleteStagingEquipment,
                  table.getSelectedRowModel().flatRows.map(({ original }) => original)
                ),
                onExit: () => setConfirmation({}),
              })
            }
            variant="contained"
          >
            Delete
          </Button>
        </Box>
      );
    },
  });

  return (
    <>
      {stagingEquipment.some(
        (e) => !e.data['auto_tagger.equip_type_preds'] && e.equipmentDevice?.protocol === 'bacnet'
      ) ? (
        <Alert severity="warning">
          Some confidence values are not yet available and will appear after our processing is
          complete
        </Alert>
      ) : null}
      <EnumSelectorDialog
        onSubmit={(et) =>
          stagingEquipmentUpdates(
            equipmentUpdateEquipmentTypes.map((e) => ({
              equip_id: e.equipId,
              equipment_type: { id: et.id, confidence: 100 },
            }))
          ).then(() => setEquipmentUpdateEquipmentTypes([]))
        }
        onCancel={() => setEquipmentUpdateEquipmentTypes([])}
        child={EquipmentTypeSelector}
        entities={equipmentUpdateEquipmentTypes}
      />
      <PublishDialog
        onExit={() => setEquipmentToPublish([])}
        equipment={equipmentToPublish}
        publishEquipment={publishEquipment}
      />
      <ConfirmDialog
        id={confirmation.id}
        content={confirmation.content}
        onSuccess={confirmation.onSuccess}
        onExit={confirmation.onExit}
      />
      <MaterialReactTable table={mrt} />
    </>
  );
};

const mapStateToProps = (state, { building }) => {
  const {
    entities: {
      staging: { equipmentLoadTime },
      equipTypes,
    },
  } = state;
  const { stagingPoints, stagingEquipment } = getBuildingStaging(state, building.id);
  return {
    equipmentLoadTime: equipmentLoadTime[building.id] ?? false,
    stagingPoints: [...stagingPoints.values()],
    stagingEquipment: [...stagingEquipment.values()],
    equipmentTypes: equipTypes.byId,
    canWrite: building.canWrite,
  };
};

const mapDispatchToProps = (dispatch, { building }) => ({
  stagingPointUpdates: (pointUpdates) => dispatch(updateStagingPoints(building.id, pointUpdates)),
  stagingEquipmentCreate: (name) => dispatch(createStagingEquipment(building.id, name)),
  stagingEquipmentUpdates: (equipUpdates) =>
    dispatch(updateStagingEquipment(building.id, equipUpdates)),
  publishEquipment: (equipmentList, topics) =>
    dispatch(publishEquipmentAction(building.id, equipmentList, topics)),
  unpublish: (equipmentIds, pointIds, relationships) =>
    dispatch(unpublishStagingAction(building.id, equipmentIds, pointIds, relationships)),
  deleteStagingEquipment: (equipIds) => dispatch(stagingEquipmentDelete(building.id, equipIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Equipment);
